import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import isEmpty from 'lodash/isEmpty';
import { getVirtualEventSession } from '../../services/api/eureka';
import { useGlobalState } from '../../utils/container';
import BookmarkButton from '../General/BookmarkButton';
import ClassifierIconLabelComponent from '../Icons/ClassifierIconLabelComponent';
import InstitutionDetailHeader from './Institution';
import PersonDetailHeader from './Person';
import TimeslotVirtualSession from './TimeslotVirtualSession';
import TimeslotDetailHeader from './Timeslot';
import NavigationLink from '../../components/Navigation/NavigationLink';
import { useTheme } from '../Theme/ThemeContext';
import { Flex } from '../../scenes/common/Flex';
import { FontIcon } from 'react-md';
import HappeningNow from '../General/HappeningNow';
import entities from '../../scenes/VirtualSession/constants/entities';
import HostedSessionMediaPlayer from '../../scenes/VirtualModeration/features/virtualFeature/components/common/HostedSessionMediaPlayer';
import MoreOptionsShareButton from '../General/MoreOptionsShareButton';

const HeaderWrapper = styled.div`
    color: rgba(0, 0, 0, 0.87);
    font-size: 24px;
    line-height: 28px;
    position: relative;
`;

const HeaderTitle = styled.div`
    padding-top: ${props =>
        props.type === 'timeslot' ? '16px' : props.type === 'person' ? '46px' : '16px;'};
    padding-left: 16px;
    padding-bottom: 8px;
    padding-right: ${props =>
        props.type !== 'person' && props.type !== 'institution' && props.type !== 'timeslot'
            ? '44px;'
            : '16px;'};
    text-align: ${props =>
        props.type === 'person' || props.type === 'institution' ? 'center;' : 'left;'};
    margin-top: ${props => (props.type === 'person' ? '30px' : '0px')};
    font-family: 'Cabin', sans-serif;
    font-size: 21px;
    line-height: 24px;
    font-style: normal;
    font-weight: bold;
    color: #1e1e1f;
`;

const HeaderSubtitle = styled.div`
    color: rgba(0, 0, 0, 0.6);
    text-align: ${props =>
        props.type === 'person' || props.type === 'institution' ? 'center;' : 'left;'};
    padding: 8px 16px 0px 16px;
    font-size: 15px;
    line-height: 24px;
    font-family: 'Roboto', sans-serif;
    font-style: normal;
`;

const Banner = styled.div`
    width: 100%;
    height: ${props => (props.fitcontent ? 'fit-content' : '224px')};
    padding: 0 16px;
    cursor: ${props => (props.pointer ? 'pointer' : 'auto')};
    margin-bottom: ${props => (props.marginbottom ? 24 : 0)}px;

    img {
        width: 100%;
        border-radius: 8px;
    }
`;

const ContentWrapper = styled.div`
    position: relative;
`;

export const BookmarkIconStyled = styled.div`
    top: 48px;
    top: ${props => props.top};
    position: absolute;
    right: 0px;
`;

const SponsorBannerWrapper = styled.div`
    width: 100%;
    height: 50px;
    padding-left: 16px;
`;

const SponsorBanner = styled.img`
    height: 100%;
`;

const PlaceContainer = styled(Flex)`
    padding: 0 16px 24px 16px;
    color: rgba(28, 28, 30, 1);
    font-family: Roboto, sans-serif;
    font-style: normal;
    font-weight: normal;
    font-size: 15px;
    line-height: 24px;

    i {
        color: rgba(28, 28, 30, 1) !important;
        font-size: 16px !important;
        margin-right: 10px;
    }
`;

const LinkStyled = styled(NavigationLink)`
    text-decoration: none !important;

    div {
        div:hover {
            color: ${props => props.primary};
            text-decoration: underline;
        }
    }
`;

const NoLink = styled.div`
    padding-bottom: 0px;
    padding-top: 0px;
`;

export const ButtonsContainer = styled(Flex)`
    padding: ${props => props.padding || '0 16px 15px 16px'} !important;

    & > *:not(:last-child) {
        margin-right: 8px;
    }
`;

const Header = props => {
    const {
        backgroundImage,
        clickMyCongress,
        endTime,
        favoriteStatus,
        id,
        params,
        picture,
        startTime,
        subtitle,
        time,
        title,
        type,
        typeParams,
        currentUserGroups,
        imageUrl,
        backgroundType,
        hasCheckin,
        notesButton,
        notesButtonLarge,
        notesTextWithAction,
        notePage,
        parentHasCustomWrapper,
        pdf,
    } = props;
    const stateCtx = useGlobalState();
    const { socket } = stateCtx;
    const [virtualEventSession, setVirtualEventSession] = useState(null);
    const [loading, setLoading] = useState(false);
    const { theme } = useTheme();
    const virtualSessionVideoUrls = virtualEventSession && virtualEventSession.videoUrls;
    const hostedSessionIntroductionVideo =
        virtualEventSession &&
        virtualEventSession.roomType === entities.virtualEventSessionEntity.roomType.roundTable &&
        virtualSessionVideoUrls &&
        virtualSessionVideoUrls.length &&
        virtualSessionVideoUrls[0];

    const refreshVirtualEventSession = async () => {
        setLoading(true);
        const virtualEventSession = await getVirtualEventSession(id);

        updateData({ virtualEventSession });
        setLoading(false);
    };

    const updateData = data => {
        const { virtualEventSession } = data;

        if (
            virtualEventSession &&
            virtualEventSession.message !== 'Not Found' &&
            !isEmpty(virtualEventSession)
        ) {
            setVirtualEventSession(virtualEventSession);
        } else {
            setVirtualEventSession(null);
        }
    };

    useEffect(() => {
        (async () => {
            if (!id) {
                return;
            }
            await refreshVirtualEventSession();
            const { socket } = stateCtx;

            if (!socket) {
                return;
            }

            socket.on(`updateData_${id}`, updateData);
        })();
        return () => {
            if (socket) {
                socket.off(`updateData_${id}`, updateData);
            }
        };
    }, [id]);

    const classifierColor = params && params.color ? params.color : '';

    const renderPlaces = (theme, places) =>
        places && places.items
            ? places.items.map(function (it) {
                  let LinkRouter = LinkStyled;
                  let navPath = '';
                  let navParams = {
                      objectClass: 'place',
                      objectId: it.id,
                  };

                  if (it.floorplan && !theme.embed) {
                      navPath = `/floorplan/${it.floorplan}/${it.id}`;
                      navParams = null;
                  } else {
                      LinkRouter = NoLink;
                  }

                  return (
                      <LinkRouter key={it.id} path={navPath} params={navParams}>
                          <PlaceContainer primary={theme.primary}>
                              <FontIcon>room</FontIcon>
                              <div>{it.name}</div>
                          </PlaceContainer>
                      </LinkRouter>
                  );
              })
            : [];

    const PlacesList = () =>
        props.places &&
        props.places.length &&
        renderPlaces(theme, props && props.places && props.places[0]);

    return (
        <HeaderWrapper>
            {(type === 'timeslot' || type === 'programelement' || type === 'timeslots') && (
                <>
                    {backgroundType === 'image' && backgroundImage && (
                        <Banner
                            fitcontent={parentHasCustomWrapper ? 1 : 0}
                            pointer={!!(params && params.backgroundUrl)}
                            onClick={
                                params && params.backgroundUrl
                                    ? () => window.open(params.backgroundUrl, '_blank')
                                    : null
                            }
                        >
                            <img src={backgroundImage} alt="BackgroundImg" />
                        </Banner>
                    )}
                    {backgroundType === 'hostedVideo' &&
                        hostedSessionIntroductionVideo &&
                        !loading && (
                            <Banner marginbottom fitcontent={parentHasCustomWrapper ? 1 : 0}>
                                <HostedSessionMediaPlayer
                                    primary
                                    url={hostedSessionIntroductionVideo}
                                />
                            </Banner>
                        )}
                    {!parentHasCustomWrapper && (
                        <>
                            <HappeningNow
                                start={startTime}
                                end={endTime}
                                color={theme.contrast}
                                margin={'16px 16px 0 16px'}
                            />
                            <HeaderTitle type={type}>{title}</HeaderTitle>
                            {subtitle && subtitle !== '' && (
                                <HeaderSubtitle type={type}>{subtitle}</HeaderSubtitle>
                            )}
                            {(type === 'timeslot' || type === 'timeslots') && (
                                <TimeslotDetailHeader time={time} start={startTime} end={endTime} />
                            )}
                        </>
                    )}
                    <PlacesList />
                </>
            )}

            <ContentWrapper>
                {type === 'person' && (
                    <PersonDetailHeader
                        picture={picture}
                        backgroundImage={typeParams.backgroundImage}
                        favoriteStatus={favoriteStatus}
                        clickMyCongress={clickMyCongress}
                    />
                )}
                {type === 'institution' && (
                    <InstitutionDetailHeader
                        picture={picture}
                        backgroundImage={backgroundImage}
                        showClickBanner={typeParams.showClickBanner}
                        params={params}
                        favoriteStatus={favoriteStatus}
                        clickMyCongress={clickMyCongress}
                    />
                )}
                {type !== 'timeslot' &&
                    type !== 'programelement' &&
                    type !== 'timeslots' &&
                    type !== 'classifier' && <HeaderTitle type={type}>{title}</HeaderTitle>}
                {!theme.kiosk &&
                    !parentHasCustomWrapper &&
                    (type === 'timeslot' || type === 'programelement' || type === 'timeslots') && (
                        <TimeslotVirtualSession
                            time={time}
                            start={startTime}
                            end={endTime}
                            id={id}
                            params={params || {}}
                            virtualEventSession={virtualEventSession}
                            type={type}
                            favoriteStatus={favoriteStatus}
                            clickMyCongress={clickMyCongress}
                            notesButton={notesButton}
                            notesButtonLarge={notesButtonLarge}
                            typeParams={typeParams}
                            currentUserGroups={currentUserGroups}
                            hasCheckin={hasCheckin}
                            pdf={pdf}
                            moreButton={
                                <MoreOptionsShareButton
                                    type={type}
                                    notesTextWithAction={notesTextWithAction}
                                    notePage={notePage}
                                    item={{
                                        name: title,
                                        time,
                                        locations: props?.places && props.places[0],
                                        startTime,
                                        endTime,
                                    }}
                                />
                            }
                        />
                    )}
                {type !== 'timeslot' &&
                    type !== 'programelement' &&
                    type !== 'timeslots' &&
                    subtitle &&
                    subtitle !== '' && <HeaderSubtitle type={type}>{subtitle}</HeaderSubtitle>}
                {type === 'institution' && <PlacesList />}
                {!theme.kiosk &&
                    type !== 'timeslot' &&
                    type !== 'programelement' &&
                    type !== 'timeslots' &&
                    type !== 'classifier' && (
                        <ButtonsContainer alignItems={'center'} justifyContent={'space-between'}>
                            <BookmarkButton
                                favoriteStatus={favoriteStatus}
                                clickMyCongress={clickMyCongress}
                            />
                            {type === 'institution' ? (
                                <MoreOptionsShareButton
                                    type={type}
                                    notesTextWithAction={notesTextWithAction}
                                    notePage={notePage}
                                    item={{
                                        name: title,
                                        time,
                                        locations: props?.places && props.places[0],
                                        startTime,
                                        endTime,
                                    }}
                                />
                            ) : (
                                <>{notesButtonLarge}</>
                            )}
                        </ButtonsContainer>
                    )}
                {type === 'classifier' && (
                    <>
                        <Flex width={'unset'} margin={'0 0 26px 30px'}>
                            {(params?.icon || classifierColor) && (
                                <ClassifierIconLabelComponent classifierId={id} iconSize={'32px'} />
                            )}
                            <HeaderTitle type={type}>{title}</HeaderTitle>
                        </Flex>
                        <ButtonsContainer alignItems={'center'} justifyContent={'space-between'}>
                            <BookmarkButton
                                favoriteStatus={favoriteStatus}
                                clickMyCongress={clickMyCongress}
                            />

                            <MoreOptionsShareButton
                                type={type}
                                notesTextWithAction={notesTextWithAction}
                                notePage={notePage}
                                item={{
                                    name: title,
                                    time,
                                    locations: props?.places && props.places[0],
                                    startTime,
                                    endTime,
                                }}
                            />
                        </ButtonsContainer>
                    </>
                )}
                {/*{imageUrl && type !== 'person' && type !== 'institution' && (*/}
                {/*    <SponsorBannerWrapper>*/}
                {/*        <SponsorBanner src={imageUrl} alt="Sponsor" />*/}
                {/*    </SponsorBannerWrapper>*/}
                {/*)}*/}
                {/*{classifierIcons.length > 0 &&*/}
                {/*    (type === 'timeslot' || type === 'programelement') && (*/}
                {/*        <IconsContainer>{classifierIcons}</IconsContainer>*/}
                {/*    )}*/}
            </ContentWrapper>
        </HeaderWrapper>
    );
};

export default Header;
